import { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useRouter } from 'next/router';
import { useFormat } from 'helpers/hooks/useFormat';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { FullPageWidthWrapper } from '../full-bleed';
import toast from 'react-hot-toast';

const normalizeForParsing = (raw: string) =>
  raw
    .trim()
    .replace(/^tel:/i, '')
    .replace(/[‐-‒–—―﹣－]/g, '-')
    .replace(/[().\s\u00A0]/g, '')
    .replace(/(?!^)\+/g, '')
    .replace(/^00/, '+')
    .replace(/(?:ext\.?|x)\d+$/i, '');

const E164 = /^\+[1-9]\d{6,14}$/;

const parseIntlOrWithDefault = (raw: string, defaultCountry = 'DE') => {
  const s = normalizeForParsing(raw);
  return parsePhoneNumberFromString(s);
};

export default function SupportBanner({ text, buttonText }: { text: string; buttonText: string }) {
  const { locale } = useRouter();
  const [open, setOpen] = useState(false);
  const { formatMessage } = useFormat({ name: 'common' });

  const schema = z.object({
    firstname: z.string().min(
      1,
      formatMessage({
        id: 'firstName.required',
        defaultMessage: 'First name is required',
      }),
    ),
    lastname: z.string().min(
      1,
      formatMessage({
        id: 'lastName.required',
        defaultMessage: 'Last name is required',
      }),
    ),
    company: z.string().min(
      1,
      formatMessage({
        id: 'company.required',
        defaultMessage: 'Company is required',
      }),
    ),
    position: z.string().min(
      1,
      formatMessage({
        id: 'position.required',
        defaultMessage: 'Position is required',
      }),
    ),
    email: z.string().email(
      formatMessage({
        id: 'email.required',
        defaultMessage: 'Valid email is required',
      }),
    ),
    phone: z
      .string()
      .trim()
      .superRefine((value, ctx) => {
        const p = parseIntlOrWithDefault(value, 'DE');
        if (p?.isValid?.()) {
          return;
        }
        const candidate = normalizeForParsing(value);
        if (E164.test(candidate)) return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: formatMessage({
            id: 'phone.required',
            defaultMessage: 'Valid phone number is required',
          }),
        });
      })
      .transform((value) => {
        const p = parseIntlOrWithDefault(value, 'DE');
        if (p?.isValid?.()) return p.number;
        return normalizeForParsing(value);
      }),
    message: z
      .string()
      .min(
        1,
        formatMessage({
          id: 'message.required',
          defaultMessage: 'Message is required',
        }),
      )
      .trim()
      .max(
        2000,
        formatMessage({
          id: 'message.max',
          defaultMessage: 'Maximum 2000 characters allowed',
        }),
      ),
  });

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: FormData) => {
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          locale,
          email: data.email,
          title: `CozyB2C Support Request - ${locale}`,
          message: data,
        }),
      });

      const result = await res.json();

      if (res.ok && result?.message === 'email sent') {
        toast.success(
          formatMessage({
            id: 'request.success',
            defaultMessage: 'Your request has been sent successfully',
          }),
        );
        reset();
        setOpen(false);
      } else {
        toast.error(
          formatMessage({
            id: 'request.failed',
            defaultMessage: 'Failed to send request. Please try again.',
          }),
        );
        console.error('Send failed:', result);
      }
    } catch (err) {
      toast.error(
        formatMessage({
          id: 'request.error',
          defaultMessage: 'An error occurred while sending your request. Please try again later.',
        }),
      );
      console.error('Send error:', err);
    }
  };

  const inputClass =
    'mt-1 w-full rounded border border-gray-300 px-3 py-2 text-sm focus:border-[#164e63] focus:ring-1 focus:ring-[#164e63]';

  return (
    <FullPageWidthWrapper>
      <div className="w-[100vw]">
        <div className="flex w-full items-center justify-center gap-2 bg-[#102B33] p-4">
          <p className="text-sm text-white">{text}</p>
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded border px-4 py-1 text-sm font-semibold text-white hover:border-[#38b2ac] hover:bg-[#38b2ac]"
          >
            <p className="text-sm text-white">{buttonText}</p>
          </button>
        </div>

        <Transition.Root show={open} as={Fragment}>
          <Dialog as="div" className="relative z-50" onClose={setOpen}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/40" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-200"
                  enterFrom="opacity-0 translate-y-2 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-2 sm:scale-95"
                >
                  <Dialog.Overlay className="w-full max-w-lg transform rounded bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold">
                      {formatMessage({
                        id: 'support.title',
                        defaultMessage: 'Let us know how we can help you the best',
                      })}
                    </Dialog.Title>
                    <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
                      <div className="flex flex-col gap-4 sm:flex-row">
                        <div className="w-full sm:w-1/2">
                          <label className="block text-sm font-medium">
                            {formatMessage({
                              id: 'firstName',
                              defaultMessage: 'First Name',
                            })}
                            *
                          </label>
                          <input type="text" {...register('firstname')} className={inputClass} />
                          {errors.firstname && <p className="mt-1 text-sm text-red-500">{errors.firstname.message}</p>}
                        </div>
                        <div className="w-full sm:w-1/2">
                          <label className="block text-sm font-medium">
                            {formatMessage({
                              id: 'lastName',
                              defaultMessage: 'Last Name',
                            })}
                            *
                          </label>
                          <input type="text" {...register('lastname')} className={inputClass} />
                          {errors.lastname && <p className="mt-1 text-sm text-red-500">{errors.lastname.message}</p>}
                        </div>
                      </div>
                      <div className="flex flex-col gap-4 sm:flex-row">
                        <div className="w-full sm:w-1/2">
                          <label className="block text-sm font-medium">
                            {formatMessage({
                              id: 'company',
                              defaultMessage: 'Company',
                            })}
                            *
                          </label>
                          <input type="text" {...register('company')} className={inputClass} />
                          {errors.company && <p className="mt-1 text-sm text-red-500">{errors.company.message}</p>}
                        </div>
                        <div className="w-full sm:w-1/2">
                          <label className="block text-sm font-medium">
                            {formatMessage({
                              id: 'position',
                              defaultMessage: 'Position',
                            })}
                            *
                          </label>
                          <input type="text" {...register('position')} className={inputClass} />
                          {errors.position && <p className="mt-1 text-sm text-red-500">{errors.position.message}</p>}
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium">
                          {formatMessage({
                            id: 'email',
                            defaultMessage: 'Email',
                          })}
                          *
                        </label>
                        <input type="email" {...register('email')} className={inputClass} />
                        {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email.message}</p>}
                      </div>
                      <div>
                        <label className="block text-sm font-medium">
                          {formatMessage({
                            id: 'phone',
                            defaultMessage: 'Phone',
                          })}
                          *
                        </label>
                        <input type="tel" {...register('phone')} className={inputClass} />
                        {errors.phone && <p className="mt-1 text-sm text-red-500">{errors.phone.message}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium">
                          {formatMessage({ id: 'message', defaultMessage: 'Message' })}*
                        </label>
                        <textarea rows={4} {...register('message')} className={inputClass} />
                        {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>}
                      </div>

                      <div className="flex justify-end gap-3">
                        <button type="button" onClick={() => setOpen(false)} className="rounded px-4 py-2 text-sm">
                          {formatMessage({
                            id: 'cancel',
                            defaultMessage: 'Cancel',
                          })}
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="rounded bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-opacity-90 disabled:opacity-60"
                        >
                          {isSubmitting
                            ? '...'
                            : formatMessage({
                                id: 'sendRequest',
                                defaultMessage: 'Send request',
                              })}
                        </button>
                      </div>
                    </form>
                  </Dialog.Overlay>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      </div>
    </FullPageWidthWrapper>
  );
}
